import { compilePattern, type CompiledPattern, type PatternDef } from './schema';

/**
 * Authored patterns. Iterate on any of these live with `?stress` (turrets
 * cycle them) — the authoring tool the design doc asks for.
 */

/** Counter-rotating double spiral; pure clockwork, fully deterministic. */
export const twinSpiral: PatternDef = {
  id: 'twinSpiral',
  duration: 5,
  emitters: [
    {
      interval: 0.06,
      angle: { kind: 'spin', start: 0, rate: 2.4 },
      shot: { arms: 2 },
      bullet: { shape: 'orb', speed: 150, radius: 5, lifetime: 7 },
    },
    {
      delay: 1.2,
      interval: 0.06,
      angle: { kind: 'spin', start: Math.PI / 2, rate: -2.4 },
      shot: { arms: 2 },
      bullet: {
        shape: 'shard', lane: 'orange',
        speed: { from: 70, to: 230, over: 2 },
        radius: 4, lifetime: 6,
      },
    },
  ],
};

/** Aimed wall with a random dodge gap, telegraphed by a line. */
export const mercyWall: PatternDef = {
  id: 'mercyWall',
  emitters: [
    {
      interval: 1.6,
      volleys: 3,
      angle: { kind: 'aimed' },
      shot: {
        spreadCount: 36,
        spread: Math.PI * 0.9,
        gap: { count: 1, width: 0.3, placement: 'random' },
        jitter: 0.008,
      },
      bullet: { shape: 'orb', speed: 200, radius: 5, lifetime: 5 },
      telegraph: { kind: 'line', lead: 0.6, length: 1000, width: 44 },
    },
  ],
};

/** Telegraphed ground slams on the player + delayed homing chasers. */
export const slamAndSeek: PatternDef = {
  id: 'slamAndSeek',
  emitters: [
    {
      interval: 2.0,
      volleys: 3,
      angle: { kind: 'fixed', angle: 0 },
      telegraph: { kind: 'zone', lead: 0.8, radius: 95, aimed: true, damage: 1 },
    },
    {
      delay: 1.0,
      interval: 2.0,
      volleys: 3,
      angle: { kind: 'randomArc', center: { kind: 'aimed' }, arc: 0.6 },
      shot: { spreadCount: 5, spread: 0.5 },
      bullet: {
        shape: 'diamond', lane: 'orange',
        speed: 130, radius: 5, lifetime: 6,
        mod: { kind: 'homing', turnRate: 1.6, duration: 1.4 },
      },
    },
  ],
};

/** Dense expanding rings — the stress-test filler pattern. */
export const stressRings: PatternDef = {
  id: 'stressRings',
  duration: 4,
  emitters: [
    {
      interval: 0.25,
      angle: { kind: 'spin', start: 0, rate: 0.7 },
      shot: { arms: 24 },
      bullet: { shape: 'orb', speed: 110, radius: 5, lifetime: 9 },
    },
  ],
};

/** Watcher: tight aimed 2-shot pair. Deliberately slow orbs — the watcher is
 *  the only ranged regular enemy and should pressure, not snipe. */
export const watcherVolley: PatternDef = {
  id: 'watcherVolley',
  emitters: [{
    interval: 0.1,
    volleys: 1,
    angle: { kind: 'aimed' },
    shot: { spreadCount: 2, spread: 0.22 },
    bullet: { shape: 'orb', speed: 185, radius: 4, lifetime: 4, scale: 0.8 },
  }],
};

/** Chanter: self-centered ground slam — area denial without bullets. */
export const chanterSlam: PatternDef = {
  id: 'chanterSlam',
  emitters: [{
    interval: 1.0,
    volleys: 1,
    angle: { kind: 'fixed', angle: 0 }, // schema-required; unused (no bullet/line)
    telegraph: { kind: 'zone', lead: 0.85, radius: 120, damage: 1 },
  }],
};

/** Brute: chanterSlam's big slow sibling — wide zone, long visible lead. */
export const bruteSlam: PatternDef = {
  id: 'bruteSlam',
  emitters: [{
    interval: 1.0,
    volleys: 1,
    angle: { kind: 'fixed', angle: 0 }, // schema-required; unused (no bullet/line)
    telegraph: { kind: 'zone', lead: 1.1, radius: 150, damage: 1 },
  }],
};

/** Boss: heavy aimed barrage — spread volleys + accelerating shard fans. */
export const bossBarrage: PatternDef = {
  id: 'bossBarrage',
  emitters: [
    {
      interval: 0.7,
      volleys: 3,
      angle: { kind: 'aimed' },
      shot: { spreadCount: 7, spread: 0.8, jitter: 0.015 },
      bullet: { shape: 'orb', speed: 210, radius: 5, lifetime: 5 },
    },
    {
      delay: 0.35,
      interval: 0.7,
      volleys: 3,
      angle: { kind: 'aimed', offset: 0 },
      shot: { spreadCount: 3, spread: 0.3 },
      bullet: {
        shape: 'shard', lane: 'orange',
        speed: { from: 90, to: 320, over: 1.6 },
        radius: 4, lifetime: 4,
      },
    },
  ],
};

/** Boss: rotating rings that force sustained movement. */
export const bossWheel: PatternDef = {
  id: 'bossWheel',
  duration: 4,
  emitters: [{
    interval: 0.16,
    angle: { kind: 'spin', start: 0, rate: 1.7 },
    shot: { arms: 6 },
    bullet: { shape: 'ring', lane: 'orange', speed: 130, radius: 5, lifetime: 7, scale: 0.9 },
  }],
};

// ---------------------------------------------------------------------------
// Boss-pool patterns (July 2026 retune). Every boss loop ends in a multi-
// second silent wait (the punish window) — density stays BELOW the old
// Warden's phase-2 overlap. Lite variants are parametrized clones.

/** Warden opening: twinSpiral at half cadence, shorter burst. */
export const twinSpiralLite: PatternDef = {
  id: 'twinSpiralLite',
  duration: 3.5,
  emitters: twinSpiral.emitters.map((e) => ({ ...e, interval: 0.12 })),
};

/** Warden finale: bossWheel thinned to a walkable ring. */
export const bossWheelLite: PatternDef = {
  id: 'bossWheelLite',
  duration: 3,
  emitters: bossWheel.emitters.map((e) => ({ ...e, interval: 0.28 })),
};

/** Bellkeeper: slow full rings with two wide dodge gaps — walls you walk
 *  through. Gap placement is the only rng draw (same class as mercyWall). */
export const bellToll: PatternDef = {
  id: 'bellToll',
  emitters: [{
    interval: 1.4,
    volleys: 3,
    angle: { kind: 'spin', start: 0, rate: 0.3 },
    shot: { arms: 14, gap: { count: 2, width: 0.55, placement: 'random' } },
    bullet: { shape: 'ring', lane: 'orange', speed: 110, radius: 5, lifetime: 7 },
  }],
};

/** Esoterium: telegraphed aimed spear — the line commits at telegraph time,
 *  so sidestepping the lance IS the dodge; the recovery is the punish. */
export const lanceVolley: PatternDef = {
  id: 'lanceVolley',
  emitters: [{
    interval: 1.8,
    volleys: 2,
    angle: { kind: 'aimed' },
    shot: { spreadCount: 5, spread: 0.12 },
    bullet: { shape: 'shard', speed: 320, radius: 4, lifetime: 4 },
    telegraph: { kind: 'line', lead: 0.7, length: 1000, width: 36 },
  }],
};

/** Esoterium finale: the same spear, faster and one extra throw. */
export const lanceStorm: PatternDef = {
  id: 'lanceStorm',
  emitters: lanceVolley.emitters.map((e) => ({ ...e, volleys: 3, interval: 1.4 })),
};

/** Gravemother: aimed zone blooms + a lazy curving 3-arm drift — the player
 *  dodges geography, not aim. */
export const gravebloom: PatternDef = {
  id: 'gravebloom',
  emitters: [
    {
      interval: 1.6,
      volleys: 3,
      angle: { kind: 'fixed', angle: 0 }, // schema-required; unused (zone-only)
      telegraph: { kind: 'zone', lead: 0.9, radius: 110, aimed: true, damage: 1 },
    },
    {
      interval: 0.5,
      duration: 4.6,
      angle: { kind: 'spin', start: 0, rate: 0.9 },
      shot: { arms: 3 },
      bullet: {
        shape: 'orb', lane: 'orange', speed: 90, radius: 5, lifetime: 7,
        mod: { kind: 'curve', rate: 0.6 },
      },
    },
  ],
};

export const PATTERN_DEFS: PatternDef[] = [
  twinSpiral, mercyWall, slamAndSeek, stressRings, watcherVolley, chanterSlam,
  bruteSlam, bossBarrage, bossWheel,
  twinSpiralLite, bossWheelLite, bellToll, lanceVolley, lanceStorm, gravebloom,
];

export function buildPatternRegistry(): Map<string, CompiledPattern> {
  const m = new Map<string, CompiledPattern>();
  for (const def of PATTERN_DEFS) m.set(def.id, compilePattern(def));
  return m;
}
